<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Une visite virtuelle Google Street View place votre entreprise sur
            Google Maps — et dans chaque résultat de recherche qui affiche votre
            fiche. Lorsqu'un client potentiel recherche votre catégorie
            d'activité à proximité, votre visite interactive 360° apparaît
            directement dans les résultats, augmentant les taux de clic et
            établissant la confiance avant même qu'il ne vous visite.
          </p>
          <p>
            Les images sont placées sur <em>votre</em> lieu Google Maps.
            <strong>Travvir est l’éditeur Street View</strong> de cet
            emplacement — sans connexion à votre compte Google perso.
          </p>
          <p>
            Un <strong>scan unique</strong> : lieu Maps, choix des scènes,
            publication. Une <strong>visite 360°</strong> : lieu Maps, puis
            publication directe. Détails produit sur la
            <a :href="`${mainSiteUrl}/google-streetview`"
              >page Google Street View</a
            >
            ; les étapes dans l’app dans
            <a href="/blog/fr/publier-google-street-view"
              >publier avec Travvir</a
            >.
          </p>
        </section>

        <section>
          <h2>Qu'est-ce qu'une visite virtuelle Google Street View ?</h2>
          <p>
            Une visite d'entreprise Google Street View est une visite virtuelle
            360° publiée sur Google Maps via votre Google Business Profile. Elle
            apparaît dans la Recherche Google, Google Maps et le panneau de
            connaissance — offrant aux internautes une vue immersive de
            l'intérieur de votre entreprise depuis le plus grand moteur de
            recherche au monde.
          </p>
        </section>

        <section>
          <h2>Bénéfices SEO des visites virtuelles Google Street View</h2>
          <p>
            Les entreprises avec des visites virtuelles Google enregistrent
            <strong>2× plus d'engagement</strong> sur leur Google Business
            Profile. La visite augmente le temps passé sur la fiche, signale à
            Google que votre profil est complet et digne de confiance, et peut
            influencer positivement votre position dans les résultats locaux.
          </p>
        </section>

        <section>
          <h2>Comment créer une visite virtuelle Google Street View</h2>
          <h3>
            Option 1 — Capturer avec Travvir, publier sur Google Street View
          </h3>
          <p>
            Créez un scan ou une visite 360° liée dans Travvir, puis
            <strong>Partager → Google Street View</strong>. Commencez par le bon
            lieu Google Maps. Pour un scan unique, sélectionnez les scènes puis
            publiez. Pour une visite, publiez directement. Travvir est l’éditeur
            de cet emplacement.
          </p>
          <h3>Option 2 — Utiliser directement l'application Street View</h3>
          <p>
            Téléchargez l'application Google Street View, appuyez sur l'icône
            caméra et suivez le processus de capture guidé. Plus simple, mais
            sans l'amélioration qualité IA de Travvir ni la liaison
            multi-pièces.
          </p>
          <h3>Processus de téléchargement étape par étape</h3>
          <p>
            1. Ouvrez le Gestionnaire Google Business Profile. 2. Sélectionnez
            "Photos" puis "Photos 360°". 3. Téléchargez vos images
            équirectangulaires (minimum 7,5 mégapixels). 4. Placez précisément
            l'épingle de localisation. 5. Soumettez pour validation —
            généralement approuvé sous 48 heures. 6. Une fois approuvé, liez la
            visite depuis la section "Visite virtuelle" de votre profil.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
            <li>
              <a :href="`${mainSiteUrl}/google-streetview`"
                >Google Street View avec Travvir</a
              >
            </li>
            <li>
              <a href="/blog/fr/publier-google-street-view"
                >Publier un scan ou une visite avec Travvir</a
              >
            </li>
            <li>
              <a href="/blog/fr/visite-virtuelle-google-business"
                >Visite virtuelle Google Business</a
              >
            </li>
          </ul>
        </section>

        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "visite-virtuelle-google-street-view",
);
const config = useRuntimeConfig();
const mainSiteUrl = config.public.baseUrl;

useBlogSeo({
  title:
    "Visite Virtuelle Google Street View : Créer et Publier en 2026 | Travvir",
  description:
    "Créez une visite virtuelle Google Street View : choisissez le lieu Maps, puis publiez un scan (scènes) ou une visite 360° directement. Travvir est l’éditeur de cet emplacement.",
  keywords:
    "visite virtuelle Google Street View, visite virtuelle Google Maps, 360 Google Business, tour virtuel Google",
  slug: "visite-virtuelle-google-street-view",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "Comment publier une visite virtuelle sur Google Street View ?",
      answer:
        "Choisissez le lieu Google Maps. Pour un scan unique, sélectionnez les scènes puis publiez ; pour une visite 360°, publiez-la directement. Travvir est l’éditeur de cet emplacement. Google valide en général sous 24–72 h.",
    },
    {
      question: "Une visite Google Street View améliore-t-elle le SEO local ?",
      answer:
        "Oui — les entreprises avec visites virtuelles Google enregistrent 2× plus d'engagement sur leur fiche et un meilleur score de notoriété qui améliore leur classement dans les résultats locaux.",
    },
  ],
});
</script>
